import { Dialog } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { ToastService } from '@libs/shared-services';
import { RadioButtonComponent } from '@libs/ui-lib';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { MarkdownPipe } from 'ngx-markdown';
import { BaaSignModalComponent, BaaSignModalData } from '../baa-sign-modal';
import { AuthorizedOption } from '../baa-sign-modal/baa-sign-modal.types';
import { BaaSignModalStore } from '../baa-sign-modal/baa-sign-modal.store';

@Component({
  selector: 'lib-ffd-notes-sign-baa',
  standalone: true,
  imports: [NgClass, TranslateModule, FormsModule, SvgIconComponent, RadioButtonComponent, LottieComponent, MarkdownPipe, AsyncPipe],
  templateUrl: './ffd-notes-sign-baa.component.html',
  styleUrls: ['./ffd-notes-sign-baa.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BaaSignModalStore],
})
export class FfdNotesSignBaaComponent implements OnInit {
  private readonly dialog = inject(Dialog);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  protected readonly store = inject(BaaSignModalStore);
  protected readonly selection = signal<AuthorizedOption | null>(null);
  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  protected readonly isPendingState = computed(() => this.store.signRequestSent() || this.store.hasPendingInvitation());
  protected readonly nextButtonTitle = computed(() => {
    if (!this.selection()) return 'express_baa_authorization3_sign_continue';

    return this.selection() === 'authorized'
      ? 'express_baa_authorization3_sign_review_and_sign'
      : 'express_baa_authorization3_sign_add_signer_details';
  });

  public ngOnInit(): void {
    this.store.loadActiveSignRequest();
  }

  protected onSelect(option: AuthorizedOption): void {
    this.selection.set(option);
  }

  protected onNext(): void {
    if (this.selection() === null) {
      this.toastService.error(this.translateService.instant('express_app_baa_authorized_select_option_error'));
      return;
    }

    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    const dialogRef = this.dialog.open(BaaSignModalComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      disableClose: true,
      width: '90vw',
      maxWidth: '520px',
      data: { location: 'home', authorizedOption: this.selection()! } satisfies BaaSignModalData,
      providers: [{ provide: BaaSignModalStore, useValue: this.store }],
    });

    dialogRef.closed.subscribe(() => {
      this.store.resetModalState();
    });
  }
}
